/**
 * Tenant Context - Multi-tenant branding and configuration
 *
 * Provides tenant information (name, branding) to all components.
 * Fetched from /api/auth/tenant on app load (no auth required).
 *
 * Usage:
 *   const { tenant, loading } = useTenant();
 *   const brandName = tenant?.branding?.name || 'AINI';
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';

// ============================================================
// Types
// ============================================================

interface TenantBranding {
  name: string;
  full_name?: string;
  domain?: string;
  privacy_email?: string;
}

interface Tenant {
  id: string;
  name: string;
  branding: TenantBranding;
  tenant_type?: 'standard' | 'institutional';
  institutional_user_id?: number;
  /** Whether E-Ink mode feature is enabled for this tenant */
  einkModeEnabled?: boolean;
  /** UI mode for this tenant (default, employee, etc.) */
  ui_mode?: string;
}

interface TenantContextType {
  tenant: Tenant | null;
  loading: boolean;
  error: string | null;
}

// ============================================================
// Context
// ============================================================

const TenantContext = createContext<TenantContextType>({
  tenant: null,
  loading: true,
  error: null,
});

// ============================================================
// Provider
// ============================================================

interface TenantProviderProps {
  children: ReactNode;
}

export function TenantProvider({ children }: TenantProviderProps): JSX.Element {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenant = async (): Promise<void> => {
      try {
        // Fetch tenant info from public endpoint (no auth required)
        interface TenantApiResponse {
          id: string;
          name: string;
          branding: TenantBranding;
          tenant_type?: 'standard' | 'institutional';
          eink_mode_enabled?: boolean;
          ui_mode?: string;
        }
        const response = await axios.get<TenantApiResponse>('/api/auth/tenant');
        // Map API response to Tenant interface (snake_case -> camelCase)
        const tenantData: Tenant = {
          id: response.data.id,
          name: response.data.name,
          branding: response.data.branding,
          tenant_type: response.data.tenant_type,
          einkModeEnabled: response.data.eink_mode_enabled ?? false,
          ui_mode: response.data.ui_mode,
        };
        setTenant(tenantData);
        setError(null);

        // Update document title with tenant name
        if (tenantData.branding?.name) {
          document.title = tenantData.branding.name;
        }
      } catch (err) {
        console.warn('Failed to fetch tenant info:', err);
        // Don't set error - use defaults
        setTenant(null);
        setError(null);
      } finally {
        setLoading(false);
      }
    };

    fetchTenant();
  }, []);

  return (
    <TenantContext.Provider value={{ tenant, loading, error }}>
      {children}
    </TenantContext.Provider>
  );
}

// ============================================================
// Hook
// ============================================================

export function useTenant(): TenantContextType {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

// ============================================================
// Utility functions
// ============================================================

/**
 * Get brand name from tenant or use default
 */
export function useBrandName(): string {
  const { tenant } = useTenant();
  return tenant?.branding?.name || tenant?.name || 'AINI';
}

/**
 * Get full brand name from tenant or use default
 */
export function useBrandFullName(): string {
  const { tenant } = useTenant();
  return tenant?.branding?.full_name || tenant?.name || 'AI-Integrated Natural Intelligence';
}

/**
 * Get privacy email from tenant or use default
 */
export function usePrivacyEmail(): string {
  const { tenant } = useTenant();
  return tenant?.branding?.privacy_email || 'privacy@aini.ai';
}
